// 지도 마커 hover 툴팁 — 화면 전체에서 단 하나만 존재하는 싱글턴 오버레이.
//
// 왜 네이티브 title 속성이 아닌가:
//   - 브라우저 기본 툴팁은 스타일을 줄 수 없고(색/배지/줄바꿈 불가), 표시까지
//     1초 이상 지연되며, OS별로 모양이 제각각이다.
//   - InfoWindow는 지도 좌표에 묶여 있어 hover처럼 빠르게 붙였다 떼기엔 무겁다.
//
// 왜 마커 content 안의 CSS :hover가 아닌가:
//   - 마커 content는 인라인 style만 쓸 수 있어(:hover 불가) 클래스를 넣어야 하고,
//   - 툴팁이 마커 DOM 안에 있으면 옆 마커(zIndex가 더 높은)에 가려진다.
//   → body 직속 + position:fixed로 띄워 항상 최상단에 오게 한다.

let root: HTMLDivElement | null = null;
let body: HTMLDivElement | null = null;
let arrow: HTMLDivElement | null = null;

// 현재 툴팁의 주인(마커 객체). 마커가 겹쳐 있으면 이벤트가
// mouseover(A) → mouseover(B) → mouseout(A) 순서로 올 수 있는데, 주인을 확인하지
// 않으면 A의 뒤늦은 mouseout이 방금 뜬 B의 툴팁을 지워버린다.
let owner: unknown = null;

// 마커의 화면상 사각형(뷰포트 좌표). x는 중앙, top/bottom은 위/아래 모서리.
export interface TooltipAnchor {
  x: number;
  top: number;
  bottom: number;
}

const FONT =
  '"Pretendard Variable", Pretendard, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';
const BG = "rgba(28,25,23,0.95)";
const GAP = 10; // 마커와 툴팁 사이 여백(px)
const EDGE = 8; // 뷰포트 가장자리 최소 여백(px)

function ensureRoot(): HTMLDivElement {
  if (root && root.isConnected) return root;

  root = document.createElement("div");
  root.setAttribute("role", "tooltip");
  Object.assign(root.style, {
    position: "fixed",
    left: "0px",
    top: "0px",
    zIndex: "10000",
    pointerEvents: "none",
    maxWidth: "268px",
    padding: "10px 12px",
    borderRadius: "12px",
    background: BG,
    border: "1px solid rgba(255,255,255,0.10)",
    boxShadow: "0 12px 30px rgba(0,0,0,0.34)",
    color: "#fff",
    fontFamily: FONT,
    fontSize: "12px",
    lineHeight: "1.45",
    textAlign: "left",
    opacity: "0",
    visibility: "hidden",
    transition: "opacity 0.12s ease",
  } as CSSStyleDeclaration);

  body = document.createElement("div");
  arrow = document.createElement("div");
  Object.assign(arrow.style, {
    position: "absolute",
    width: "10px",
    height: "10px",
    background: BG,
    transform: "rotate(45deg)",
    borderRadius: "2px",
  } as CSSStyleDeclaration);

  root.appendChild(body);
  root.appendChild(arrow);
  document.body.appendChild(root);

  // 스크롤/리사이즈로 마커가 움직이면 앵커가 어긋난다 — 그냥 숨긴다.
  window.addEventListener("scroll", forceHide, true);
  window.addEventListener("resize", forceHide);

  return root;
}

function forceHide(): void {
  hideMarkerTooltip();
}

// nextOwner를 주면 그 마커가 툴팁의 주인이 된다(hide 시 대조용).
export function showMarkerTooltip(html: string, anchor: TooltipAnchor, nextOwner?: unknown): void {
  if (!html) return;
  const el = ensureRoot();
  owner = nextOwner ?? null;
  body!.innerHTML = html;

  // 크기를 재려면 먼저 레이아웃에 올려야 한다. 화면 밖에서 측정한 뒤 최종 위치로.
  el.style.visibility = "hidden";
  el.style.left = "-9999px";
  el.style.top = "0px";
  const w = el.offsetWidth;
  const h = el.offsetHeight;

  const left = clamp(anchor.x - w / 2, EDGE, Math.max(EDGE, window.innerWidth - w - EDGE));
  let top = anchor.top - h - GAP;
  const below = top < EDGE;
  if (below) top = anchor.bottom + GAP;

  el.style.left = `${Math.round(left)}px`;
  el.style.top = `${Math.round(top)}px`;

  // 화살표는 (클램프된) 카드 안에서 마커 중심을 가리키게 x를 다시 계산한다.
  const ax = clamp(anchor.x - left, 14, Math.max(14, w - 14)) - 5;
  arrow!.style.left = `${Math.round(ax)}px`;
  arrow!.style.top = below ? "-5px" : `${h - 5}px`;

  el.style.visibility = "visible";
  el.style.opacity = "1";
}

// byOwner를 주면 그 마커가 현재 주인일 때만 닫는다(겹친 마커 사이 깜빡임 방지).
// 인자 없이 부르면 무조건 닫는다 — 지도 조작/언마운트 등 전역 사유.
export function hideMarkerTooltip(byOwner?: unknown): void {
  if (!root) return;
  if (byOwner !== undefined && owner !== byOwner) return;
  owner = null;
  root.style.opacity = "0";
  root.style.visibility = "hidden";
}

function clamp(v: number, min: number, max: number): number {
  return v < min ? min : v > max ? max : v;
}

// ── 마커 바인딩 ─────────────────────────────────────────────────────

// 마커에 hover 툴팁을 붙인다. HTML은 marker.__tooltip에서 매번 다시 읽으므로
// setIcon으로 마커 내용이 갱신돼도(선택/dim 변화) 최신 내용이 뜬다.
export function attachMarkerTooltip(naver: any, marker: any, html?: string): void {
  if (html !== undefined) marker.__tooltip = html;

  naver.maps.Event.addListener(marker, "mouseover", (e: any) => {
    const tip = marker.__tooltip;
    if (!tip) return;
    const anchor = markerAnchor(marker) || pointerAnchor(e);
    if (anchor) showMarkerTooltip(tip, anchor, marker);
  });
  naver.maps.Event.addListener(marker, "mouseout", (e: any) => {
    // 마커 내부(자식 요소)로 이동한 mouseout은 무시 — mouseover/mouseout은
    // 부모↔자식 이동에서도 발화하므로 그대로 닫으면 깜빡인다.
    const el = markerEl(marker);
    const to = e?.pointerEvent?.relatedTarget ?? e?.domEvent?.relatedTarget;
    if (el && to instanceof Node && el.contains(to)) return;
    hideMarkerTooltip(marker);
  });
  naver.maps.Event.addListener(marker, "click", () => hideMarkerTooltip());
}

// 지도를 움직이면 마커도 같이 움직인다 — 떠 있던 툴팁은 앵커가 어긋나므로 숨긴다.
// (드래그 중에는 마커 mouseout이 오지 않을 수 있어 지도 이벤트로도 받는다.)
// 반환값은 리스너 해제 함수.
export function bindMapTooltipHide(naver: any, map: any): () => void {
  const listeners = ["dragstart", "zoom_start", "zoom_changed", "click"].map(
    (ev) => naver.maps.Event.addListener(map, ev, forceHide),
  );
  return () => {
    listeners.forEach((l) => { try { naver.maps.Event.removeListener(l); } catch {} });
    hideMarkerTooltip();
  };
}

function markerEl(marker: any): HTMLElement | null {
  try {
    return typeof marker.getElement === "function" ? marker.getElement() : null;
  } catch { return null; }
}

// 마커 DOM의 실제 화면 사각형. 툴팁을 마커 바로 위에 정확히 물리기 위해 쓴다.
function markerAnchor(marker: any): TooltipAnchor | null {
  const r = markerEl(marker)?.getBoundingClientRect?.();
  if (r && (r.width || r.height)) return { x: r.left + r.width / 2, top: r.top, bottom: r.bottom };
  return null;
}

// getElement를 못 쓰는 SDK 버전 폴백 — 커서 위치를 앵커로.
function pointerAnchor(e: any): TooltipAnchor | null {
  const p = e?.pointerEvent ?? e?.domEvent ?? e?.originalEvent;
  if (typeof p?.clientX !== "number") return null;
  return { x: p.clientX, top: p.clientY - 14, bottom: p.clientY + 14 };
}
